import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { ChevronRight, ExternalLink, X } from 'lucide-react';
import * as PersonApi from '../personAdmin/PersonAdminApi';
import ChangeSetsCard from '../personAdmin/ChangeSetsCard';
import ContactEmailEdit from '../personAdmin/ContactEmailEdit';
import ResearchCard from '../personAdmin/ResearchCard';
import { FIELD_GROUPS, errorText } from '../personAdmin/personAdminModel';
import { valueText } from '../personAdmin/personAdminParts';
import { mediaUrl } from '../../utils/mediaUrl';
import {
  MiniButton, Panel, PanelClose, PanelEyebrow, PanelHead, PanelHint, PanelSection, PanelSectionTitle, PanelTitle, PersonMeta,
} from './programAdminStyles';
import { RoleText, StateTag } from './StateTag';
import { invitedByOf, nodeTitle } from './programAdminModel';
import { PERMISSIONS, useCan } from '../authority/permissions';

// The person's face in the header: the photo, or the initials of the program name on a tinted disc.
const Avatar = ({ name, photoUrl }) => {
  const initials = String(name || '').split(/[\s,]+/).filter((w) => /^\p{L}/u.test(w) && !/\.$/.test(w))
    .slice(0, 2).map((w) => w[0].toUpperCase()).join('');
  const box = { width: '2.75rem', height: '2.75rem', borderRadius: '50%', flex: '0 0 auto' };
  return photoUrl
    ? <img src={mediaUrl(photoUrl)} alt="" referrerPolicy="no-referrer" style={{ ...box, objectFit: 'cover', border: '1px solid #cbd5e1' }} />
    : <div aria-hidden="true" style={{ ...box, background: '#e0e7ff', color: '#3730a3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '.95rem' }}>{initials}</div>;
};

// Shown elsewhere in the panel already: the name is its title, the address has its own row, the
// photo sits next to the profile, the institution under the name.
const SHOWN_ABOVE = new Set(['title', 'firstName', 'lastName', 'titleSuffix', 'contactEmail', 'photoUrl', 'institution']);
const LINK_FIELDS = new Set(['linkedinUrl', 'websiteUrl', 'instagramUrl', 'facebookUrl']);

// The person's profile as it stands, read-only and compact: the filled fields only, by the person
// detail screen's groups. Editing stays on the full profile.
const ProfileSummary = ({ person }) => {
  const intl = useIntl();
  const t = (id, def) => intl.formatMessage({ id, defaultMessage: def });
  const photoUrl = person.fields?.photoUrl?.value;
  const groups = FIELD_GROUPS
    .map((group) => ({
      id: group.id,
      rows: group.fields
        .filter((field) => !SHOWN_ABOVE.has(field.key))
        .map((field) => ({ key: field.key, text: valueText(t, field.key, person.fields?.[field.key]?.value) }))
        .filter((row) => row.text),
    }))
    .filter((group) => group.rows.length > 0);
  return (
    <>
      <PanelSectionTitle><FormattedMessage id="programAdmin.panel.person.profileTitle" defaultMessage="Profile" /></PanelSectionTitle>
      <div style={{ display: 'flex', gap: '.75rem', alignItems: 'flex-start' }}>
        {photoUrl && (
          <img src={mediaUrl(photoUrl)} alt="" referrerPolicy="no-referrer"
            style={{ width: '4.5rem', height: '4.5rem', objectFit: 'cover', border: '1px solid #cbd5e1', flex: '0 0 auto' }} />
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          {groups.length === 0 && (
            <PanelHint style={{ marginTop: 0 }}>
              <FormattedMessage id="programAdmin.panel.person.profileEmpty" defaultMessage="Nothing filled in yet beyond the name and email." />
            </PanelHint>
          )}
          {groups.map((group) => (
            <div key={group.id} style={{ marginBottom: '.55rem' }}>
              <div style={{ fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.04em', color: '#64748b', margin: '0 0 .15rem' }}>
                <FormattedMessage id={`personAdmin.group.${group.id}`} defaultMessage={group.id} />
              </div>
              {group.rows.map((row) => (
                <div key={row.key} style={{ display: 'flex', gap: '.5rem', fontSize: '.8rem', lineHeight: 1.45, color: '#0f172a' }}>
                  <span style={{ flex: '0 0 7rem', color: '#64748b' }}>
                    <FormattedMessage id={`personAdmin.field.${row.key}`} defaultMessage={row.key} />
                  </span>
                  {LINK_FIELDS.has(row.key) ? (
                    <a href={row.text} target="_blank" rel="noreferrer" style={{ color: '#0369a1', overflowWrap: 'anywhere' }}>{row.text}</a>
                  ) : (
                    <span style={{
                      minWidth: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                      display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }} title={row.text.length > 200 ? row.text : undefined}>{row.text}</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

// A person, inside the program screen (opened by clicking a name on a spot): who they are, their
// contact email (editable right here when it is missing), whether they have an account, a link to
// the full profile, their profile as it stands (read-only), and the two
// cards the person detail screen has for the work that happens around a new person - AI research and
// the changes it proposed, to accept or reject. It reads the person itself (GET /api/cms/people/{id})
// and reads it again after every decision, edit or finished research; `onChanged` then tells the
// screen to re-read the program, because an applied value can change what the program prints.
const PersonPanel = ({ personId, spots = [], language, onOpenSpot, onChanged, onClose }) => {
  const intl = useIntl();
  const can = useCan();
  const [person, setPerson] = useState(null);
  const [error, setError] = useState(null);
  const seq = useRef(0);

  const load = useCallback(async () => {
    const token = ++seq.current;
    try {
      const detail = await PersonApi.fetchPerson(personId);
      if (seq.current !== token) return;
      setPerson(detail);
      setError(null);
    } catch (err) {
      if (seq.current === token) setError(err);
    }
  }, [personId]);

  useEffect(() => {
    load();
    return () => { seq.current += 1; };
  }, [load]);

  // After a decision, an edit or finished research: this panel first, then the program behind it.
  const changed = useCallback(async () => {
    await load();
    await onChanged?.();
  }, [load, onChanged]);

  const institution = person?.fields?.institution?.value;

  return (
    <Panel aria-label={intl.formatMessage({ id: 'programAdmin.panel.person.label', defaultMessage: 'Selected person' })}>
      <PanelHead>
        <div style={{ display: 'flex', gap: '.65rem', alignItems: 'center' }}>
          {person && <Avatar name={person.programName} photoUrl={person.fields?.photoUrl?.value} />}
          <div>
            <PanelEyebrow><FormattedMessage id="programAdmin.panel.person.eyebrow" defaultMessage="Person" /></PanelEyebrow>
            <PanelTitle>{person ? person.programName : '…'}</PanelTitle>
            {institution && <PersonMeta>{institution}</PersonMeta>}
          </div>
        </div>
        <PanelClose type="button" onClick={onClose} aria-label={intl.formatMessage({ id: 'programAdmin.panel.close', defaultMessage: 'Close the panel' })}>
          <X size={18} />
        </PanelClose>
      </PanelHead>

      {!person && !error && (
        <PanelSection><PanelHint><FormattedMessage id="programAdmin.panel.person.loading" defaultMessage="Loading the person…" /></PanelHint></PanelSection>
      )}
      {error && !person && (
        <PanelSection>
          <p role="alert" style={{ margin: 0, padding: '.6rem .7rem', background: '#fee2e2', color: '#991b1b', fontSize: '.82rem' }}>
            {errorText(error, intl.formatMessage({ id: 'personAdmin.detail.failed', defaultMessage: 'Could not load this person.' }))}
          </p>
        </PanelSection>
      )}

      {person && (
        <>
          <PanelSection>
            <div style={{ fontSize: '.85rem', color: '#334155' }}>
              <ContactEmailEdit personId={personId} value={person.fields?.contactEmail?.value} editable={!person.account}
                onSaved={(updated) => { setPerson(updated); onChanged?.(); }} />
            </div>
            <div style={{ marginTop: '.35rem', fontSize: '.78rem', color: '#64748b' }}>
              {person.account
                ? <FormattedMessage id="personAdmin.detail.hasAccount" defaultMessage="Has an account: {email}" values={{ email: person.account.email }} />
                : <FormattedMessage id="personAdmin.detail.noAccount" defaultMessage="No account" />}
            </div>
            {/* The full profile is a staff-area screen; an editor works with the person through this
                panel and the program only. */}
            {can(PERMISSIONS.staffArea) && (
              <div style={{ marginTop: '.5rem', fontSize: '.8rem' }}>
                <Link to={`/admin/people/${personId}`} style={{ color: '#0369a1', display: 'inline-flex', gap: '.3rem', alignItems: 'center' }}>
                  <ExternalLink size={12} aria-hidden="true" />
                  <FormattedMessage id="programAdmin.panel.person.profile" defaultMessage="Open full profile" />
                </Link>
              </div>
            )}
          </PanelSection>

          {spots.length > 0 && (
            <PanelSection>
              <PanelSectionTitle><FormattedMessage id="programAdmin.panel.person.spots" defaultMessage="Spots at this event" /></PanelSectionTitle>
              {spots.map(({ assignment, entry }) => {
                const invitedBy = invitedByOf(assignment);
                return (
                  <div key={assignment.id} style={{ display: 'flex', alignItems: 'center', gap: '.45rem', flexWrap: 'wrap', fontSize: '.8rem', margin: '0 0 .35rem' }}>
                    <RoleText role={assignment.role} />
                    <span style={{ fontWeight: 600 }}>{nodeTitle(entry, language)}</span>
                    <StateTag status={assignment.status} />
                    <MiniButton type="button" onClick={() => onOpenSpot?.(assignment.id)} style={{ marginLeft: 'auto' }}>
                      <FormattedMessage id="programAdmin.panel.person.openSpot" defaultMessage="Open spot" /><ChevronRight size={13} aria-hidden="true" />
                    </MiniButton>
                    {invitedBy && (
                      <span style={{ flexBasis: '100%', color: '#64748b' }} title={invitedBy}>
                        <FormattedMessage id="programAdmin.people.invitedBy" defaultMessage="Invited by {name}" values={{ name: invitedBy }} />
                      </span>
                    )}
                  </div>
                );
              })}
            </PanelSection>
          )}

          <PanelSection><ProfileSummary person={person} /></PanelSection>

          <PanelSection><ResearchCard personId={personId} onChanged={changed} /></PanelSection>

          <PanelSection>
            <ChangeSetsCard personId={personId} changeSets={person.changeSets} hasAccount={!!person.account} onChanged={changed} />
          </PanelSection>
        </>
      )}
    </Panel>
  );
};

export default PersonPanel;
